// Eskom PARCC — pure logic for the editable Transaction detail.
//
// React-free, Firebase-free helpers so they unit-test in isolation:
//   • `canEditTransaction` — the security boundary. A reviewer may edit a
//     transaction ONLY when it is allocated to their own email (case-insensitive,
//     via `normalizeEmail`); an admin may edit any. This mirrors the Working
//     Papers `scopeWorkingPapers` rule — own-allocation-only for reviewers, null
//     role / blank email → most restrictive (no edit).
//   • The finding-draft model (`FindingDraft`) + builders the editor's local
//     state uses: seed a draft from a read finding, make a blank draft, apply the
//     domain classifier's suggestion when the finding text changes, and turn the
//     draft list into the `FindingInsertInput[]` payload the replace-per-tx write
//     wrapper accepts. No classification or compliance maths is reimplemented —
//     `applySuggestion` delegates to the domain `classifyFinding`.
import { classifyFinding, type TxContext } from '../../domain/classify';
import type { RuleReferences } from '../../domain/rulebook/ruleReferences';
import type { Transaction } from '../../data/types';
import type { FindingRead } from '../../lib/db';
import type { FindingInsertInput } from '../../lib/dbWrite';

// The edit-permission boundary now lives in the capability model (src/lib/
// permissions): admin edits anything; reviewer/teamLead edit their own
// allocation; dataCoordinator edits their own LPO allocations; manager never
// edits. Re-exported here so existing callers keep importing it from this module.
export { canEditTransaction } from '../../lib/permissions';

/**
 * Is this transaction reviewed, for DISPLAY purposes?
 *
 * The editor dual-writes the Reviewed toggle: the boolean `reviewed` column AND
 * `reviewStatus: 'Reviewed'` (the connector update mutation now accepts both). The
 * displayed flag derives from EITHER signal so it round-trips regardless of which
 * was set: a transaction counts as reviewed when its `reviewed` boolean is true OR
 * its `reviewStatus` reads "Reviewed" (case-insensitively, trimmed). Pure and
 * side-effect-free so the register column and the detail row agree on one rule.
 */
export function isReviewed(tx: Pick<Transaction, 'reviewed' | 'reviewStatus'>): boolean {
  if (tx.reviewed === true) return true;
  return (tx.reviewStatus ?? '').trim().toLowerCase() === 'reviewed';
}

/**
 * The editor's working copy of a finding line. Carries a stable client-side
 * `key` (for React list reconciliation across add/delete), every editable field,
 * and the classifier-supplied metadata (`suggestedCompliance`, `anchors`,
 * `route`, `confidence`, `matchedRules`, `rationale`). `seeded` is preserved for
 * existing lines and forced false for user-entered ones (see `newFindingDraft`).
 */
export interface FindingDraft {
  /** Stable client-side identity for the editor list (NOT the DB id). */
  key: string;
  /** The DB id when this draft maps to an existing finding; absent for new ones. */
  id?: string;
  text: string;
  finalCompliance: string;
  anchors: string[];
  rationale: string;
  reviewNotes: string;
  // Classifier-derived metadata, carried through the replace payload.
  suggestedCompliance: string;
  confidence: string;
  route: string;
  matchedRules: string[];
  /** Regulatory references (Eskom policy / PFMA / law) from the matched rule. */
  regulatoryRefs?: RuleReferences;
  /** The working-paper procedure the finding was captured against (R3 #2) —
   *  read-only carry so a replace-per-tx save never strips it. */
  criterion?: string;
  source?: string;
  legacyStatus?: string;
  effectiveStatus?: string;
  /** Closure lifecycle (RTM Workstream I). Set by the override cascade (closedVia
   *  'override') and carried from a read finding, so a re-save preserves — not
   *  re-stamps — an existing closure. Absent while the finding is outstanding. */
  closedAt?: string;
  closedVia?: string;
  seeded: boolean;
}

let draftKeySeq = 0;
/** Monotonic client-side key for a new draft row. */
function nextDraftKey(): string {
  draftKeySeq += 1;
  return `draft-${draftKeySeq}`;
}

/** Seed an editable {@link FindingDraft} from a read finding. */
export function findingToDraft(f: FindingRead): FindingDraft {
  return {
    key: f.id || nextDraftKey(),
    id: f.id,
    text: f.text ?? '',
    finalCompliance: f.finalCompliance ?? '',
    anchors: [...(f.anchors ?? [])],
    rationale: f.rationale ?? '',
    reviewNotes: f.reviewNotes ?? '',
    suggestedCompliance: f.suggestedCompliance ?? '',
    confidence: f.confidence ?? '',
    route: f.route ?? '',
    matchedRules: [...(f.matchedRules ?? [])],
    regulatoryRefs: f.regulatoryRefs,
    criterion: f.criterion,
    source: f.source,
    legacyStatus: f.legacyStatus,
    effectiveStatus: f.effectiveStatus,
    closedAt: f.closedAt,
    closedVia: f.closedVia,
    seeded: f.seeded === true,
  };
}

/** A fresh, blank, user-entered draft (never seeded). */
export function newFindingDraft(): FindingDraft {
  return {
    key: nextDraftKey(),
    text: '',
    finalCompliance: '',
    anchors: [],
    rationale: '',
    reviewNotes: '',
    suggestedCompliance: '',
    confidence: '',
    route: '',
    matchedRules: [],
    seeded: false,
  };
}

/** The transaction context the classifier reads (mechanism + value). */
export function txContextOf(tx: Pick<Transaction, 'mechanism' | 'value'>): TxContext {
  return { mechanism: tx.mechanism, value: tx.value };
}

/**
 * Re-run the domain classifier for a draft's `text` and pre-fill its
 * classifier-derived metadata: `suggestedCompliance`, `anchors`, `route`,
 * `confidence`, `matchedRules`. Also seeds `finalCompliance`/`rationale` from the
 * suggestion *only while the reviewer has not set them yet* — once a reviewer has
 * typed a rationale or chosen a final compliance, re-classifying on a later text
 * tweak must not clobber their decision. Pure: delegates to `classifyFinding`,
 * reimplements no classification.
 */
export function applySuggestion(draft: FindingDraft, text: string, ctx: TxContext): FindingDraft {
  const result = classifyFinding(text, ctx);
  return {
    ...draft,
    text,
    suggestedCompliance: result.level,
    anchors: [...result.anchors],
    route: result.route,
    confidence: result.confidence,
    matchedRules: [...result.matchedRules],
    regulatoryRefs: result.references,
    // Pre-fill the reviewer-facing fields only when still empty (don't clobber edits).
    finalCompliance: draft.finalCompliance === '' ? result.level : draft.finalCompliance,
    rationale: draft.rationale === '' && result.label ? result.label : draft.rationale,
  };
}

/**
 * Build the `FindingInsertInput[]` payload for {@link replaceFindingsForTx} from
 * the editor's draft list. Drafts whose text is blank after trimming are dropped
 * (an empty "Add finding" row the reviewer never filled in). User-entered lines
 * carry `seeded: false`; existing lines keep their original `seeded` flag.
 */
export function draftsToInsertInputs(drafts: readonly FindingDraft[]): FindingInsertInput[] {
  return drafts
    .filter((d) => d.text.trim() !== '')
    .map((d) => ({
      text: d.text.trim(),
      source: d.source,
      legacyStatus: d.legacyStatus,
      anchors: d.anchors,
      suggestedCompliance: d.suggestedCompliance || undefined,
      finalCompliance: d.finalCompliance || undefined,
      rationale: d.rationale || undefined,
      confidence: d.confidence || undefined,
      route: d.route || undefined,
      matchedRules: d.matchedRules,
      effectiveStatus: d.effectiveStatus,
      reviewNotes: d.reviewNotes || undefined,
      regulatoryRefs: d.regulatoryRefs,
      criterion: d.criterion,
      // Closure lifecycle (RTM Workstream I) — carried so the reconcile write path
      // persists an override-cascade closure (and preserves an existing one).
      closedAt: d.closedAt,
      closedVia: d.closedVia,
      seeded: d.seeded,
    }));
}
